import { ArrowDownward } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  ICompanyFormation,
  ICompanyFormationFullObject,
} from '../../Interfaces/ICompanyFormation';
import { IDocumentDirectory } from '../../Interfaces/IDocumentDirectory';
import { IDocumentDirectoryHasDocument } from '../../Interfaces/IDocumentDirectoryHasDocument';
import { IInheritFullObject } from '../../Interfaces/IInherit';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import {
  ILegalTransactionDataCollection,
  ILegalTransactionFullObject,
} from '../../Interfaces/ILegalTransaction';
import { ILegalTransactionRelationship } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { LegalTransactionRelationshipTypeEnum } from '../../Interfaces/ILegalTransactionRelationship/LegalTransactionRelationshipTypeEnum';
import { IMortgage, IMortgageFullObject } from '../../Interfaces/IMortgage';
import { CompanyFormationMain } from '../CompanyFormation/CompanyFormationMain';
import { InheritMain } from '../Inherit/InheritMain';
import { LandRegisterMain } from '../LandRegisterQuery/LandRegisterMain';
import { MortgageMain } from '../Mortgage/MortgageMain';
import { PurchaseContractMain } from '../PurchaseContract/PurchaseContractMain';
import { AddLegalTransactionRelationshipDialog } from './AddLegalTransactionRelationshipDialog';
import { LegalTransactionRelationshipPresenter } from './generic/LegalTransactionRelationshipPresenter';

import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';

const DataCollectionAccordion: React.FC<{
  title: string;
  children: React.ReactElement;
}> = ({ title, children }) => (
  <Accordion>
    <AccordionSummary expandIcon={<ArrowDownward />}>
      <Typography>{title}</Typography>
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

const isCompanyFormation = (
  dataCollection: ILegalTransactionDataCollection
): dataCollection is ICompanyFormationFullObject => {
  return 'idCompanyFormation' in dataCollection;
};

const isInherit = (
  dataCollection: ILegalTransactionDataCollection
): dataCollection is IInheritFullObject => {
  return 'idInherit' in dataCollection;
};

const isMortgage = (
  dataCollection: ILegalTransactionDataCollection
): dataCollection is IMortgageFullObject => {
  return 'idMortgage' in dataCollection;
};

export const dataCollectionTitle = (
  dataCollection: ILegalTransactionDataCollection
) => {
  if (isCompanyFormation(dataCollection)) {
    return companyFormationTitle(dataCollection);
  } else if (isInherit(dataCollection)) {
    return dataCollection.PersonArray.length > 0
      ? `Erbscheinsantrag - ${dataCollection.PersonArray[0].FirstName} ${dataCollection.PersonArray[0].LastName}`
      : 'Erbscheinsantrag';
  } else if (isMortgage(dataCollection)) {
    return dataCollection.RealEstate.Street &&
      dataCollection.RealEstate.StreetNr
      ? `Grundschuld - ${dataCollection.RealEstate.Street} ${dataCollection.RealEstate.StreetNr}`
      : 'Grundschuld';
  }
  return '';
};

export const issueTitle = (relationship: ILegalTransactionRelationship) => {
  if (
    relationship.idRelationshipType ===
    LegalTransactionRelationshipTypeEnum.PurchaseContract
  ) {
    if (relationship.RealEstateArray.length === 0) return 'Kaufvertrag';
    const { Street, StreetNr, Longitude, Latitude } =
      relationship.RealEstateArray[0];
    return `Kaufvertrag - ${Street || Longitude} ${StreetNr || Latitude}`;
  } else if (
    relationship.idRelationshipType ===
    LegalTransactionRelationshipTypeEnum.LandRegisterQuery
  ) {
    return `Grundbuchauszug`;
  }
  return '';
};

const companyFormationTitle = (companyFormation: ICompanyFormation) => {
  let prefix = 'Unternehmens';
  let sufix = 'änderung';

  if (companyFormation.idBusinessForm === 12) {
    prefix = 'Vereins';
  }
  if (companyFormation.isFounding) {
    sufix = 'gründung';
  }
  return companyFormation.CompanyName
    ? `${prefix}${sufix} - ${companyFormation.CompanyName}`
    : `${prefix}${sufix}`;
};

const isGenericPresenterEnabled = (
  relationship: ILegalTransactionRelationship
): boolean => {
  const enabledRelationshipTypes = [
    LegalTransactionRelationshipTypeEnum.SignatureCertification,
    LegalTransactionRelationshipTypeEnum.Generic,
  ];
  return enabledRelationshipTypes.includes(relationship.idRelationshipType);
};

interface IProps {
  legalTransaction: ILegalTransactionFullObject;
  relationships: ILegalTransactionRelationship[];
  setRelationships: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
  dataCollection: ILegalTransactionDataCollection[];
  setDataCollection: React.Dispatch<
    React.SetStateAction<ILegalTransactionDataCollection[]>
  >;
  assignedLegalClients: ILegalClient[];
  setAssignedLegalClients: React.Dispatch<React.SetStateAction<ILegalClient[]>>;
  isSavePossible: (boolean | Record<string, boolean>)[];
  setIsSavePossible: React.Dispatch<
    React.SetStateAction<(boolean | Record<string, boolean>)[]>
  >;

  documentArray: IDocumentDirectoryHasDocument[];
  documentDirectoryArray: IDocumentDirectory[];
  setDocumentArray: Function;
  setDocumentDirectoryArray: Function;

  legalPhaseFullObjectArray: ILegalPhaseFullObject[];
  setLegalPhaseFullObjectArray: Function;
  legalReqStates: ILegalRequirementState[];
}

export const LegalTransactionDataCollection: React.FC<IProps> = props => {
  const {
    legalTransaction,
    relationships,
    setRelationships,
    dataCollection,
    setDataCollection,
    assignedLegalClients,
    setAssignedLegalClients,
    isSavePossible,
    setIsSavePossible,
    documentArray,
    documentDirectoryArray,
    setDocumentArray,
    setDocumentDirectoryArray,
    legalPhaseFullObjectArray,
    setLegalPhaseFullObjectArray,
    legalReqStates,
  } = props;
  const [isMount, setIsMount] = useState(false);

  const changeArray = (updatedObject: any, idx: number) => {
    const tmpArray = [...dataCollection];
    tmpArray[idx] = updatedObject;
    setDataCollection(tmpArray);
  };

  const setRelationship = (newRelationship: ILegalTransactionRelationship) =>
    setRelationships(x =>
      x.map(r =>
        r.idRelationship === newRelationship.idRelationship
          ? newRelationship
          : r
      )
    );

  return (
    <>
      <Typography variant='h5' sx={{ mb: 3 }}>
        Datenerfassung
        <Button
          sx={{ float: 'right' }}
          variant='outlined'
          onClick={() => setIsMount(true)}
        >
          Hinzufügen
        </Button>
      </Typography>

      {!dataCollection.length && !relationships.length && (
        <>
          <i>Keine Datenerfassung vorhanden </i>
        </>
      )}

      {relationships.map((r, i) => {
        if (
          r.idRelationshipType ===
          LegalTransactionRelationshipTypeEnum.PurchaseContract
        ) {
          return (
            <DataCollectionAccordion title={issueTitle(r)} key={i}>
              <PurchaseContractMain
                key={i}
                relationship={r}
                setRelationship={setRelationship}
                takenLegalClients={assignedLegalClients}
                setTakenLegalClients={setAssignedLegalClients}
              />
            </DataCollectionAccordion>
          );
        } else if (
          r.idRelationshipType ===
          LegalTransactionRelationshipTypeEnum.LandRegisterQuery
        ) {
          return (
            <DataCollectionAccordion title={issueTitle(r)} key={i}>
              <LandRegisterMain
                legalTransaction={legalTransaction}
                key={i}
                relationship={r}
                setRelationship={setRelationship}
                documentArray={documentArray}
                documentDirectoryArray={documentDirectoryArray}
                setDocumentArray={setDocumentArray}
                setDocumentDirectoryArray={setDocumentDirectoryArray}
                legalPhaseFullObjectArray={legalPhaseFullObjectArray}
                setLegalPhaseFullObjectArray={setLegalPhaseFullObjectArray}
                legalReqStates={legalReqStates}
              />
            </DataCollectionAccordion>
          );
        }

        if (isGenericPresenterEnabled(r)) {
          return (
            <LegalTransactionRelationshipPresenter
              relationship={r}
              setRelationship={setRelationship}
              key={i}
              assignedLegalClients={assignedLegalClients}
              setAssignedLegalClients={setAssignedLegalClients}
            />
          );
        }
      })}

      {dataCollection.map((x, i) => {
        if ('idCompanyFormation' in x) {
          return (
            <DataCollectionAccordion title={dataCollectionTitle(x)} key={i}>
              <CompanyFormationMain
                key={`datacollection-element-${i}`}
                companyFormationObject={x}
                setCompanyFormationObject={(updatedObject: ICompanyFormation) =>
                  changeArray(updatedObject, i)
                }
              />
            </DataCollectionAccordion>
          );
        } else if ('idInherit' in x) {
          return (
            <DataCollectionAccordion title={dataCollectionTitle(x)} key={i}>
              <InheritMain
                inheritObject={x}
                setInheritObject={(updatedObject: IInheritFullObject) =>
                  changeArray(updatedObject, i)
                }
              />
            </DataCollectionAccordion>
          );
        } else if ('idMortgage' in x) {
          return (
            <DataCollectionAccordion title={dataCollectionTitle(x)} key={i}>
              <MortgageMain
                mortgageObject={x}
                setMortgageObject={(updatedObject: IMortgage) =>
                  changeArray(updatedObject, i)
                }
              />
            </DataCollectionAccordion>
          );
        }
      })}

      {isMount && (
        <AddLegalTransactionRelationshipDialog
          {...props}
          unmount={() => setIsMount(false)}
        />
      )}
    </>
  );
};
